/**
 * Fase 2 KPI event schemas.
 * Single source of truth for analytics event shapes.
 */

export interface KpiEventSchema {
  event: string;
  requiredParams: string[];
  description: string;
}

export const FASE2_KPI_EVENTS: KpiEventSchema[] = [
  {
    event: 'commuter_route_opened_from_pinned',
    requiredParams: ['dock', 'sessionId'],
    description: 'User opens dock page from pinned header',
  },
  {
    event: 'confidence_details_clicked',
    requiredParams: ['level', 'source', 'sessionId'],
    description: 'User clicks "Se detaljer" on maybe/no confidence badge',
  },
  {
    event: 'blank_state_shown',
    requiredParams: ['sessionId'],
    description: 'Discovery/blank state shown (no results, no favorites)',
  },
  {
    event: 'offline_cache_used',
    requiredParams: ['dock', 'sessionId'],
    description: 'Offline fallback activated, cached data displayed',
  },
  {
    event: 'push_opt_in_clicked',
    requiredParams: ['dock', 'sessionId'],
    description: 'User clicks push notification opt-in toggle',
  },
  {
    event: 'push_permission_granted',
    requiredParams: ['sessionId'],
    description: 'Browser notification permission granted',
  },
  {
    event: 'push_permission_denied',
    requiredParams: ['sessionId'],
    description: 'Browser notification permission denied',
  },
  {
    event: 'push_gate_evaluated',
    requiredParams: ['ok', 'reason', 'dockSlug', 'confidence', 'relMin', 'source', 'sessionId'],
    description: 'Push gate evaluated (dry-run decision)',
  },
  {
    event: 'push_gate_blocked',
    requiredParams: ['reason', 'dockSlug', 'source', 'sessionId'],
    description: 'Push gate blocked a notification',
  },
  {
    event: 'push_gate_would_send',
    requiredParams: ['dockSlug', 'confidence', 'relMin', 'source', 'sessionId'],
    description: 'Push gate would have sent a notification',
  },
  {
    event: 'push_prefs_updated',
    requiredParams: ['enabled', 'sessionId'],
    description: 'User updated push preferences',
  },
  {
    event: 'push_sw_error',
    requiredParams: ['message', 'sessionId'],
    description: 'Service worker registration error',
  },
  {
    event: 'push_sent',
    requiredParams: ['dockSlug', 'sessionId'],
    description: 'Push notification successfully sent',
  },
  {
    event: 'push_failed',
    requiredParams: ['dockSlug', 'errorCode', 'sessionId'],
    description: 'Push notification failed to send',
  },
  {
    event: 'push_unsubscribed',
    requiredParams: ['sessionId'],
    description: 'User unsubscribed from push notifications',
  },
];

/**
 * Validate that a given event payload matches the expected schema.
 * Useful for debug mode / tests.
 */
export function validateEventParams(
  event: string,
  params: Record<string, unknown>
): { valid: boolean; missing: string[] } {
  const schema = FASE2_KPI_EVENTS.find(e => e.event === event);
  if (!schema) return { valid: true, missing: [] }; // Unknown events pass through

  // sessionId is auto-injected by trackEvent, so we skip it
  const missing = schema.requiredParams
    .filter(p => p !== 'sessionId')
    .filter(p => !(p in params));

  return { valid: missing.length === 0, missing };
}
